import Nes from 'nes';

export type WebSocketHandlerDto = (event: string, message: any, flag: any) => void;

type ConstructorOptsDto = {
  headers?: any;
  rootPath?: string;
  token?: string;
  noAuth?: boolean;
}

export type SubscriptionReturns = {
  unsub: () => void
}

const MAX_CONNECTION_RETRY: number = 10;

/**
 * WSCLIENT create and instantiate websocket connection
 * Subscribe and unsubscribe on demand
 * There 2 map container -> One with subscribed channels and one with unsubscribed channels
 * Used to reconnect with channels without reload the app
 * NOTE : THIS CLASS CAN USED ONLY ON BO WITHOUT MODIFICATION
 * NOTE2 : TO USE IN OTHER APP IMPLEMENT THE ENGINE !!
 */
class WsClient {
  private _name: string = null;
  private _client: Nes.Client = null;
  private _server: string = null;
  private _headers: any = {};
  private _disconnecting: boolean = false;
  private _isConnectionRetrying: boolean = false;
  private _currentRetryingAttempt: number = 0;
  private _connected: boolean = false;
  private _stopReconnect: boolean = false;
  private _unsubscribed: Map<string, WebSocketHandlerDto[]> = new Map();
  private _subscribed: Map<string, WebSocketHandlerDto[]> = new Map();
  private _proxyfier: (msg: any, flag: any) => void = this.proxyfierFn.bind(this);
  private _rootPath: string = '/';
  private _token: string = null;
  private _noAuth: boolean = false;

  /**
   * Constructor prepare all needed arguments to establish connection
   * @param name server name to connect
   * @param server URL to server
   * @param opts headers in auth ; rootPath => root of subscribtion ; token => jwt token
   */
  public constructor(name: string, server: string, opts?: ConstructorOptsDto) {
    this._name = name;
    this._server = server;
    this._headers = opts?.headers || this._headers;
    this._rootPath = opts?.rootPath || this._rootPath;
    this._noAuth = opts?.noAuth || this._noAuth;

    if (this._rootPath.charAt(this._rootPath.length - 1) !== '/') {
      this._rootPath = `${this._rootPath}/`;
    }
  }

  /**
   * All message from server is proxyfied to this method
   * It redirect on good handler callback function
   * @param msg Data received from server
   * @param flag Websocket flag
   */
  private proxyfierFn(msg, flag): void {
    if (!msg?.event) return null; // Ignoring...
    if (this._rootPath !== '/') {
      msg.event = msg.event.replace(this._rootPath, '');
    }

    const handlers = this._subscribed.get(msg.event);

    if (handlers) {
      handlers.forEach((hdler) => {
        hdler(msg.event, msg, flag);
      });
    }
  }

  /**
   * This method will construct the headers object from given args like token and merge with given headers in constructor
   */
  private constructHeaders() {
    let headers;

    if (this._token && !this._noAuth) {
      headers = {
        auth: {
          headers: {
            ...this._headers,
            Authorization: `Bearer ${this._token}`
          }
        }
      };
    } else {
      headers = {};
    }

    return headers;
  }

  /**
   * Method used to transfer some subscription to unsubscription and vice versa
   * @param from Map container source
   * @param to Map container target
   * @param which some channels to transfer srce to trgt
   */
  private subscriberPasser(from: Map<string, WebSocketHandlerDto[]>, to: Map<string, WebSocketHandlerDto[]>, which: string[] = []) {
    if (which.length === 0) {
      if ([...from.keys()].length === 0) {
        return true;
      }

      return this.subscriberPasser(from, to, [...from.keys()]);
    }

    which.forEach((key) => {
      const tmp = from.get(key);
      if (tmp) {
        const toTmp = to.get(key);

        if (toTmp) {
          to.set(key, [...toTmp, ...tmp]);
        } else {
          to.set(key, tmp);
        }

        from.delete(key);
      }
    });

    return true;
  }

  /**
   * Subscribe to given channel and save it with his handler
   * @param key Channel
   * @param handler Function (callback)
   * @param subscribeNow Subscribe directly after save - default true;
   */
  public async addSubscription(key: string, handler: WebSocketHandlerDto, subscribeNow: boolean = true): Promise<SubscriptionReturns> {
    if (this._unsubscribed.has(key)) {
      const unsubHdlrs = this._unsubscribed.get(key);
      if (unsubHdlrs.indexOf(handler) === -1) {
        unsubHdlrs.push(handler);
      }
    } else {
      this._unsubscribed.set(key, [handler]);
    }

    if (subscribeNow && this._connected) {
      await this.subscribeTo(key);
    }

    return {
      unsub: () => this.removeSubscription(key, handler)
    };
  }

  /**
   * Unsubscribe to given channel and unsave it
   * @param key Channel
   * @param handler Function (callback)
   */
  public async removeSubscription(key: string, handler: WebSocketHandlerDto) {
    if (this._unsubscribed.has(key)) {
      this._unsubscribed.set(key, this._unsubscribed.get(key).filter((hdlers) => hdlers !== handler));
      return;
    }

    if (this._subscribed.has(key)) {
      this._subscribed.set(key, this._subscribed.get(key).filter((hdlers) => hdlers !== handler));
      if (this._subscribed.get(key).length === 0) {
        await this.unsubscribeTo(key);
        this._subscribed.delete(key);
      }
    }
  }

  private async subscribeTo(key: string, disableSubscriberPasser: boolean = false) {
    if (!this._connected) {
      throw new Error(`[WEBSOCKET] Trying to subscribe from disconnected server.`)
    }

    try {
      await this._client.subscribe(`${this._rootPath}${key}`.replace('//', '/'), this._proxyfier);
      if (!disableSubscriberPasser) {
        this.subscriberPasser(this._unsubscribed, this._subscribed, [key]);
      }
    } catch (err) {
      throw new Error(`Failed to susbscribe ${key} with error : ${err}`);
    }
  }

  private async unsubscribeTo(key: string) {
    try {
      await this._client.unsubscribe(`${this._rootPath}${key}`.replace('//', '/'), this._proxyfier);
    } catch (err) {
      throw new Error(`Failed to unsusbscribe ${key} with error : ${err}`);
    }
  }

  /**
   * This method will get all asked channels and subscribe to it
   */
  public startSubscribing() {
    if (!this._connected) return false;

    this._unsubscribed.forEach(async (_, key) => {
      await this.subscribeTo(key, true);
    });

    this.subscriberPasser(this._unsubscribed, this._subscribed);

    return true;
  }

  /**
   * On connection handler - called after websocket successfully connected
   */
  private onConnectHandler(): void {
    this._connected = true;
    this._disconnecting = false;
    // Let's get subscribe
    this.startSubscribing();
  }

  /**
   * On disconnection handler - called after websocket disconnection
   * @param _
   * @param log
   */
  private onDisconnectHandler(_, log: any): void {
    this.subscriberPasser(this._subscribed, this._unsubscribed);
    this._disconnecting = false;
    this._connected = false;

    if (!this._stopReconnect) {
      this.retryConnect();
    }

    if (log.reason) {
      //console.log(`FOR REASON : ${log.reason}`);
    }
  }

  private onErrorHandler(err): void {
    console.error(`[ws-client] error : ${err.reason}`);
  }

  /**
   * This method will retry connect to server if failed - 10 attempts max
   */
  private retryConnect() {
    if (!this._isConnectionRetrying) {
      this._isConnectionRetrying = true;
      this._currentRetryingAttempt++;

      if (this._currentRetryingAttempt <= MAX_CONNECTION_RETRY) {
        let retryIn = 4000 + (1000 * this._currentRetryingAttempt);
        setTimeout(async () => {
          await this.connect();
        }, retryIn)
      } else {
        console.error(`Max attempts reached. (${MAX_CONNECTION_RETRY}). Connection aborted.`)
      }
    }
  }

  /**
   * Connection method to the given server
   * @param opts update opts before connect like token ?
   */
  public async connect(opts?: { token?: string }) {
    this._connected = false;
    this._disconnecting = false;
    this._token = opts?.token || this._token;

    try {
      this.disconnect(false);
      this._client = new Nes.Client(this._server);
      this._client.onConnect = this.onConnectHandler.bind(this);
      this._client.onDisconnect = this.onDisconnectHandler.bind(this);
      this._client.onError = this.onErrorHandler.bind(this);
      await this._client.connect({ ...this.constructHeaders(), reconnect: false });
      this._isConnectionRetrying = false;
      this._currentRetryingAttempt = 0;
      this._stopReconnect = false;
    } catch (err) {
      this._isConnectionRetrying = false;
      this.retryConnect();
      throw new Error(`Trying to connect websocket to server ${this._server} failed with error : ${err}`);
    }
  }

  /**
   * Disconnection methods
   */
  public disconnect(clear: boolean = true) {
    if (clear) {
      this._unsubscribed = new Map();
      this._subscribed = new Map();
    }
    this._stopReconnect = true;
    this._client?.disconnect();
  }

  /**
   * @return boolean Is connected to the server ???
   */
  public isConnected() {
    return this._connected;
  }
}

export default WsClient;
